import React, { useState, useEffect } from 'react';
import { Mail, Send, Loader, AlertCircle, CheckCircle, Plus, Trash2 } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './AdminPages.css';

const EmailWorkOrders = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [sending, setSending] = useState(false);

  // Form state
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailType, setEmailType] = useState('summary');
  const [customMessage, setCustomMessage] = useState('');

  useMountEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please login to access this page');
      setLoading(false);
      return;
    }
    fetchData();
  });

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch work orders
      const woResponse = await apiCall('/api/work-orders?limit=100');
      if (woResponse.ok) {
        const woData = await woResponse.json();
        // API returns array directly
        setWorkOrders(Array.isArray(woData) ? woData : []);
      }

      // Fetch email history
      const emailResponse = await apiCall('/api/email-work-orders');
      if (emailResponse.ok) {
        const emailData = await emailResponse.json();
        setEmailHistory(Array.isArray(emailData) ? emailData : []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderSelection = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      if (newSelected.size < 5) {
        newSelected.add(orderId);
      } else {
        setError('Maximum 5 work orders can be selected');
        return;
      }
    }
    setSelectedOrders(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === workOrders.length) {
      setSelectedOrders(new Set());
    } else {
      const first5Orders = workOrders.slice(0, 5).map(wo => wo._id);
      setSelectedOrders(new Set(first5Orders));
      if (workOrders.length > 5) {
        setError('Limited to first 5 work orders (maximum allowed per email)');
      }
    }
  };

  const refreshEmailHistory = async () => {
    try {
      const emailResponse = await apiCall('/api/email-work-orders');
      if (emailResponse.ok) {
        const emailData = await emailResponse.json();
        setEmailHistory(Array.isArray(emailData) ? emailData : []);
      }
    } catch (err) {
      console.error('Error refreshing email history:', err);
    }
  };

  const handleSendEmail = async () => {
    if (selectedOrders.size === 0) {
      setError('Please select at least one work order');
      return;
    }

    if (selectedOrders.size > 5) {
      setError('Maximum 5 work orders can be sent in a single email');
      return;
    }

    if (!recipientEmail) {
      setError('Please enter recipient email');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setSending(true);
      const response = await apiCall('/api/email-work-orders/send', {
        method: 'POST',
        body: JSON.stringify({
          workOrderIds: Array.from(selectedOrders),
          recipientEmail,
          emailType,
          customMessage
        })
      });

      setSuccess(`✓ ${response.queued} email(s) queued with status "SENT". Click Refresh to check delivery status.`);
      setSelectedOrders(new Set());
      setRecipientEmail('');
      setCustomMessage('');
      setEmailType('summary');

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error sending emails:', err);
      setError('Failed to queue emails');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1><Mail size={28} /> Email Work Orders</h1>
          <p>Send work orders via email</p>
        </div>
        <div className="form-section" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader size={32} className="spinner" style={{ margin: '0 auto', color: '#3b82f6' }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Loading work orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Mail size={28} /> Email Work Orders</h1>
          <p>Send work orders via email to clients and stakeholders</p>
        </div>
      </div>

      {error && (
        <div className="status-message error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="close-btn">✕</button>
        </div>
      )}

      {success && (
        <div className="status-message success">
          <CheckCircle size={16} /> {success}
          <button onClick={() => setSuccess('')} className="close-btn">✕</button>
        </div>
      )}

      <div className="form-section">
        <div className="section-header">
          <h3>📧 Send Email</h3>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
            Recipient Email
          </label>
          <input
            type="email"
            placeholder="recipient@example.com"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
            Email Type
          </label>
          <select
            value={emailType}
            onChange={(e) => setEmailType(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          >
            <option value="summary">📋 Work Order Summary</option>
            <option value="status_update">📊 Status Update</option>
            <option value="completion">✅ Completion Notification</option>
          </select>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
            Custom Message (Optional)
          </label>
          <textarea
            placeholder="Add any additional message to include in the email..."
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box',
              minHeight: '100px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        <button
          className="btn btn-primary"
          onClick={handleSendEmail}
          disabled={sending || selectedOrders.size === 0 || selectedOrders.size > 5}
          style={{ width: '100%' }}
        >
          {sending ? (
            <>
              <Loader size={16} className="spinner" /> Sending {selectedOrders.size} email(s)...
            </>
          ) : selectedOrders.size > 5 ? (
            <>
              <Send size={16} /> Maximum 5 work orders allowed (Selected: {selectedOrders.size})
            </>
          ) : (
            <>
              <Send size={16} /> Send Email to {selectedOrders.size} Selected Order(s)
            </>
          )}
        </button>
      </div>

      <div className="form-section" style={{ marginTop: '24px' }}>
        <div className="section-header">
          <h3>📋 Work Orders ({workOrders.length})</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
              Selected: {selectedOrders.size}
            </span>
          </div>
        </div>

        {workOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <Mail size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No work orders found</p>
          </div>
        ) : (
          <div className="collections-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedOrders.size === workOrders.length && workOrders.length > 0}
                      onChange={toggleSelectAll}
                      title="Select all work orders"
                    />
                  </th>
                  <th>Trace ID</th>
                  <th>Customer</th>
                  <th>Report Type</th>
                  <th>Status</th>
                  <th>Files</th>
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.map(wo => (
                  <tr key={wo._id}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedOrders.has(wo._id)}
                        onChange={() => toggleOrderSelection(wo._id)}
                      />
                    </td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {wo.traceId?.substring(0, 12)}...
                      </span>
                    </td>
                    <td>{wo.customerName}</td>
                    <td>{wo.reportType}</td>
                    <td>
                      <span className={`status-badge status-${wo.status === 'completed' ? 'success' : wo.status === 'failed' ? 'error' : 'info'}`}>
                        {wo.status}
                      </span>
                    </td>
                    <td>{wo.fileCount}</td>
                    <td>
                      <div style={{ width: '80px', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${wo.fileProgress || 0}%`,
                            background: '#3b82f6',
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>{wo.fileProgress || 0}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="form-section" style={{ marginTop: '24px' }}>
        <div className="section-header">
          <h3>📨 Email History ({emailHistory.length})</h3>
          <button
            className="section-action-btn"
            onClick={refreshEmailHistory}
            style={{ background: '#3b82f6', color: 'white', border: 'none' }}
          >
            🔄 Refresh
          </button>
        </div>

        {emailHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <Mail size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p style={{ fontSize: '14px' }}>No emails sent yet</p>
          </div>
        ) : (
          <div className="collections-table">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>PDFs</th>
                  <th>Sent Date</th>
                </tr>
              </thead>
              <tbody>
                {emailHistory.slice(0, 20).map((email, idx) => (
                  <tr key={idx} title={email.reason ? `Reason: ${email.reason}` : ''}>
                    <td>{email.email}</td>
                    <td>{email.emailType || 'summary'}</td>
                    <td>
                      <div>
                        <span className={`status-badge ${email.status === 'Sent' ? 'status-info' : email.status === 'Delivered' ? 'status-success' : 'status-error'}`}>
                          {email.status === 'Sent' ? 'Sent' : email.status === 'Delivered' ? '✓ Delivered' : '✗ Failed'}
                        </span>
                        {email.reason && email.status === 'Failed' && (
                          <div style={{ fontSize: '11px', color: '#991b1b', marginTop: '4px', maxWidth: '200px' }}>
                            {email.reason}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: '12px', color: '#64748b' }}>
                      {email.pdfAttachments > 0 ? `${email.pdfAttachments} PDF(s)` : '-'}
                    </td>
                    <td style={{ fontSize: '12px', color: '#64748b' }}>
                      {email.sentAt ? new Date(email.sentAt).toLocaleString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailWorkOrders;
